// path-info
// ==========

import {nativeBridge} from "../Bridge_Native.js";

export class MediaPathInfo {
	constructor(data?: Partial<MediaPathInfo>) {
		Object.assign(this, data);
	}
	seriesStr: string|n;
	seasonStr: string|n;
	episodeStr: string|n;
}
/**
 * Important note: These fields are only "valid" when derived from a path following the standard frame-streaming folder-structure!
 * Example: "Pictures/LF/Frames/S1/EP1/A-B/00-01-10.0.png" ->
 * {
 * 	seasonStr: "S1", episodeStr: "EP1", timeStr: "00-01-10.0.png", timeStr_asSeconds: 70.0,
 * 	characterInitialsStr: "Alice-Bob", characterInitials: ["Alice", "Bob"],
 * }
 **/
export class ImagePathInfo extends MediaPathInfo {
	constructor(data?: Partial<ImagePathInfo>) {
		super(data);
		Object.assign(this, data);
	}
	timeStr: string|n;
	timeStr_asSeconds: number|n;
	characterInitialsStr: string|n;
	characterInitials: string[];
}
export class AudioPathInfo extends MediaPathInfo {}
export class VideoPathInfo extends MediaPathInfo {}

// images
// ==========

export class ImageFileEntry {
	contentUri: string;
	filePath: string;
	width: number;
	height: number;

	// added by js-side
	filePathInfo: ImagePathInfo;
}
export function ParseImagePath(imagePath: string): ImagePathInfo {
	const pathParts = imagePath.split("/");
	const episodePartIndex = pathParts.findIndex(part=>part.match(/^EP\d/i) != null);

	const seriesStr = pathParts[episodePartIndex - 2] as string|n;
	const seasonStr = pathParts[episodePartIndex - 1] as string|n;
	const episodeStr = pathParts[episodePartIndex] as string|n;
	const folderExistsBetweenEpisodeFolderAndFilename = episodePartIndex < pathParts.length - 2;
	const characterInitialsStr = folderExistsBetweenEpisodeFolderAndFilename ? pathParts[episodePartIndex + 1] as string|n : null;

	const timeStr = pathParts[pathParts.length - 1] as string|n;
	// example: 00-01-10.0.png
	const timeStr_parts = /^(\d{2})-(\d{2})-(\d{2})\.(\d)\.[^.]+$/.exec(timeStr ?? "");
	const timeStr_asSeconds = timeStr_parts == null ? null : (
		(parseInt(timeStr_parts[1]) * 60 * 60) +
		(parseInt(timeStr_parts[2]) * 60) +
		(parseInt(timeStr_parts[3])) +
		(parseInt(timeStr_parts[4]) / 10)
	);
	const characterInitials = characterInitialsStr?.split("-") ?? [];
	return new ImagePathInfo({seriesStr, seasonStr, episodeStr, characterInitialsStr, timeStr, timeStr_asSeconds, characterInitials});
}

export async function GetImageFileEntries_LowLevel(filePathSubstring: string) {
	const result = (await nativeBridge.Call("GetImageFileEntries", filePathSubstring)) as ImageFileEntry[];
	for (const entry of result) {
		entry.filePathInfo = ParseImagePath(entry.filePath);
	}
	return result;
}

// audio
// ==========

export class AudioFileEntry {
	contentUri: string;
	filePath: string;
	duration: number; // in ms

	// added by js-side
	filePathInfo: AudioPathInfo;
}
export function ParseAudioPath(audioPath: string): AudioPathInfo {
	const pathParts = audioPath.split("/");
	const seriesStr = pathParts[pathParts.length - 3] as string|n;
	const seasonStr = pathParts[pathParts.length - 2] as string|n;
	const fileName = pathParts[pathParts.length - 1] as string|n;
	// example: EP1.ogg (we require the "EP" prefix, so that we can distinguish "frames-audio" files from other files)
	const [matchStr, episodeNumberStr, ext] = /^EP(.+)\.([^.]+)$/.exec(fileName ?? "") ?? [null, null, null];
	const episodeStr = `EP${episodeNumberStr}`;
	return new AudioPathInfo({seriesStr, seasonStr, episodeStr});
}

export async function GetAudioFileEntries_LowLevel(filePathSubstring: string) {
	const result = (await nativeBridge.Call("GetAudioFileEntries", filePathSubstring)) as AudioFileEntry[];
	for (const entry of result) {
		entry.filePathInfo = ParseAudioPath(entry.filePath);
	}
	return result;
}

// video
// ==========

export class VideoFileEntry {
	contentUri: string;
	filePath: string;
	duration: number; // in ms
	size: number; // in bytes?

	// added by js-side
	filePathInfo: VideoPathInfo;
}
export function ParseVideoPath(videoPath: string): VideoPathInfo {
	const pathParts = videoPath.split("/");
	const seriesStr = pathParts[pathParts.length - 3] as string|n;
	const seasonStr = pathParts[pathParts.length - 2] as string|n;
	const fileName = pathParts[pathParts.length - 1] as string|n;
	// example: EP1.mp4 (we require the "EP" prefix, so that we can distinguish "frames-video" files from other files)
	const [matchStr, episodeNumberStr, ext] = /^EP(.+)\.([^.]+)$/.exec(fileName ?? "") ?? [null, null, null];
	const episodeStr = `EP${episodeNumberStr}`;
	return new VideoPathInfo({seriesStr, seasonStr, episodeStr});
}

export async function GetVideoFileEntries_LowLevel(filePathSubstring: string) {
	const result = (await nativeBridge.Call("GetVideoFileEntries", filePathSubstring)) as VideoFileEntry[];
	for (const entry of result) {
		entry.filePathInfo = ParseVideoPath(entry.filePath);
	}
	return result;
}